import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { Section, SectionHeading, FadeIn } from "../components/Section";
import Button from "../components/Button";
import { SERVICES } from "../data/content";

const Services = () => {
  return (
    <>
      <PageHero
        overline="Our Services"
        title="A full-spectrum energy partner."
        description="From advanced pipeline engineering to efficient petroleum trading, streamlined logistics and sustainable natural gas distribution — designed to empower industries and drive economic growth across Nigeria and Africa."
        image="https://images.pexels.com/photos/5884386/pexels-photo-5884386.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
        testid="services-hero"
      />

      <Section spacing="py-16 md:py-24">
        <div className="space-y-24 md:space-y-32">
          {SERVICES.map((s, i) => {
            const reversed = i % 2 === 1;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
                className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
                data-testid={`service-detail-${i}`}
              >
                <div
                  className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}
                >
                  <div className="relative overflow-hidden border border-border group">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4 grid place-items-center w-12 h-12 rounded-sm bg-primary text-primary-foreground">
                      <s.Icon size={22} weight="bold" />
                    </div>
                  </div>
                </div>
                <div
                  className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}
                >
                  <div className="font-mono text-xs text-primary mb-4">
                    Service / {s.number}
                  </div>
                  <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-[1.05]">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
                    {s.description}
                  </p>

                  <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                    {[
                      "Engineering-led delivery",
                      "Safety-first operations",
                      "Strategic partner network",
                      "Long-term reliability",
                    ].map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground/85"
                      >
                        <CheckCircle
                          weight="fill"
                          size={18}
                          className="text-primary mt-0.5"
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    to="/contact"
                    variant="outline"
                    iconRight={ArrowUpRight}
                    className="mt-8"
                  >
                    Discuss this service
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* CTA */}
      <Section
        className="bg-secondary text-secondary-foreground"
        spacing="py-20 md:py-28"
      >
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline text-primary mb-4">— Partner with us</div>
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-[1.05]">
              Building energy infrastructure for the next generation.
            </h2>
            <p className="mt-5 max-w-2xl text-secondary-foreground/80 leading-relaxed">
              Tell us about your project — pipelines, gas supply, fuel
              logistics, drilling support — and we'll respond with a tailored
              plan and timeline.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Button to="/contact" size="lg" iconRight={ArrowUpRight}>
              Start a conversation
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Services;
